import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import { Layout } from './components/Layout';
import { AdminLogin } from './pages/AdminLogin';
import { TrialRequests } from './pages/TrialRequests';
import { Organizations } from './pages/Organizations';
import { PlatformAdmins } from './pages/PlatformAdmins';
import { Crons } from './pages/Crons';
import { OrganizationDetailsEnhanced as OrganizationDetails } from './pages/OrganizationDetailsEnhanced';
import { WorkflowMigration } from './pages/WorkflowMigration';
import { Pricing } from './pages/Pricing';
import { Plans } from './pages/Plans';
import { LLMPricingPage } from './pages/LLMPricing';
import { Usage } from './pages/usage';
import { ActivityPage } from './pages/ActivityPage';
import VoiceEvals from './pages/VoiceEvals';

const SessionBootstrap: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">
    Checking your session…
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, stage } = useAdminAuth();

  // On a reload the session is restored from the HttpOnly refresh cookie, which
  // takes a round trip. Bouncing to /login first would throw away the deep link
  // the admin actually opened.
  if (stage === 'loading') {
    return <SessionBootstrap />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAdminAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/trial-requests" replace /> : <AdminLogin />
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/trial-requests" replace />} />
        <Route path="trial-requests" element={<TrialRequests />} />
        <Route path="organizations" element={<Organizations />} />
        <Route path="organizations/:id" element={<OrganizationDetails />} />
        <Route path="workflow-migration" element={<WorkflowMigration />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="plans" element={<Plans />} />
        <Route path="llm-pricing" element={<LLMPricingPage />} />
        <Route path="usage" element={<Usage />} />
        <Route path="activity" element={<ActivityPage />} />
        <Route path="platform-admins" element={<PlatformAdmins />} />
        <Route path="crons" element={<Crons />} />
        <Route path="voice-evals" element={<VoiceEvals />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AdminAuthProvider>
      <AppRoutes />
    </AdminAuthProvider>
  );
}

export default App;

